import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { SearchIcon, LocationMarkerIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon } from '@heroicons/react/outline';
import JobsLayout, { INK } from './JobsLayout';
import { LoadingBlock, LoadingOverlay, Spinner } from './Spinner';
import { COMMUNITIES, api, communityLabel, placePath, prettyDate, provinceBySlug, provinceName, setTitle, takeEmbedded } from './shared';

// /jobs, /jobs/in/<province>, /jobs/in/<province>/<city> (GROWTH-004). Filters live in the URL, so
// every search is shareable; the edge function serves the first page's data with the HTML.

const TYPES = ['Full-time', 'Part-time'];

function readFilters(search, params) {
  const sp = new URLSearchParams(search);
  const page = Number(sp.get('page') || 0);
  return {
    q: sp.get('q') || '',
    location: sp.get('location') || '',
    category: TYPES.includes(sp.get('category')) ? sp.get('category') : '',
    community: COMMUNITIES.some((c) => c.slug === sp.get('community')) ? sp.get('community') : '',
    page: Number.isInteger(page) && page >= 0 ? page : 0,
    province: params.province ? provinceBySlug(params.province) : sp.get('province') || '',
    city: params.city || '',
  };
}

function toApiParams(f) {
  const p = { name: 'search' };
  ['q', 'location', 'category', 'community', 'province', 'city'].forEach((k) => {
    if (f[k]) p[k] = f[k];
  });
  if (f.page) p.page = String(f.page);
  return p;
}

export function JobCard({ job }) {
  const where = [job.city, job.province].filter(Boolean).join(', ') || (job.remote ? 'Remote, Canada' : 'Canada');
  const cls =
    'group block rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:p-5';
  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-[Poppins] text-lg font-semibold leading-snug text-slate-900 group-hover:text-primary">{job.title}</h3>
          <p className="mt-0.5 truncate text-sm text-slate-600">{[job.company, where].filter(Boolean).join(' · ')}</p>
        </div>
        {!job.open && <span className="flex-shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">Closed</span>}
        {job.external && <ExternalLinkIcon className="h-5 w-5 flex-shrink-0 text-slate-400" aria-hidden="true" />}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
        {job.salary && <span className="font-semibold text-slate-900">{job.salary}</span>}
        {job.category && <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{job.category}</span>}
        {job.remote && <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">Remote</span>}
        {!job.external && job.open && (
          // Our own postings (native apply on canadajobleads.ca), set apart from aggregated listings.
          <span className="rounded-md bg-primary px-2 py-0.5 text-xs font-semibold text-white">Direct apply</span>
        )}
        {job.communities.slice(0, 3).map((c) => (
          <span key={c} className="rounded-md border border-red-200 px-2 py-0.5 text-xs font-medium text-primary">
            {communityLabel(c)}
          </span>
        ))}
        {job.external && <span className="text-xs text-slate-500">via {job.source}</span>}
        {job.postedOn && <span className="ml-auto text-xs text-slate-500">Posted {prettyDate(job.postedOn)}</span>}
      </div>
    </>
  );
  return (
    <li>
      {job.external ? (
        // External (GROWTH-005): the employer's own posting, opened in a new tab.
        <a href={job.path} target="_blank" rel="noopener noreferrer nofollow" className={cls} aria-label={`${job.title} at ${job.company} (opens the employer's site)`}>
          {body}
        </a>
      ) : (
        <Link to={job.path} className={cls}>
          {body}
        </Link>
      )}
    </li>
  );
}

function Chip({ active, onClick, children, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition ${
        active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-500'
      }`}
    >
      {children}
      {count !== undefined && <span className={active ? 'text-white/70' : 'text-slate-400'}>{count}</span>}
    </button>
  );
}

export default function JobSearch() {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const f = useMemo(() => readFilters(location.search, params), [location.search, params]);
  const key = location.pathname + location.search;
  const [state, setState] = useState(() => {
    const data = takeEmbedded(key);
    return data ? { key, result: data.result, places: data.places || [], error: '' } : { key: '', result: null, places: [], error: '' };
  });
  const [q, setQ] = useState(f.q);
  const [where, setWhere] = useState(f.location);
  const [showFilters, setShowFilters] = useState(false); // mobile only; always shown on large screens

  useEffect(() => {
    setQ(f.q);
    setWhere(f.location);
  }, [f.q, f.location]);

  useEffect(() => {
    if (state.key === key) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, error: '', loading: true }));
    Promise.all([api(toApiParams(f)), state.places.length ? Promise.resolve(state.places) : api({ name: 'places' }).catch(() => [])])
      .then(([result, places]) => !cancelled && setState({ key, result, places, error: '' }))
      .catch((e) => !cancelled && setState((s) => ({ ...s, key, error: e.message, loading: false })));
    return () => {
      cancelled = true;
    };
  }, [key]);

  const placeCity = params.city && state.result && state.result.results[0] ? state.result.results[0].city : params.city;
  const placeName = f.province ? (params.city ? `${placeCity}, ${f.province}` : provinceName(f.province)) : '';
  const heading = placeName ? `Jobs in ${placeName}` : f.q ? `“${f.q}” jobs in Canada` : 'Jobs across Canada';

  useEffect(() => {
    setTitle(`${placeName ? `Jobs in ${placeName}` : 'Jobs in Canada'} | CanadaJobLeads`);
  }, [placeName]);

  // Changes one or more filters; resets to page 0 unless the page itself changes.
  function update(changes) {
    const next = { ...f, page: 0, ...changes };
    const sp = new URLSearchParams();
    if (next.q) sp.set('q', next.q);
    if (next.location) sp.set('location', next.location);
    if (next.category) sp.set('category', next.category);
    if (next.community) sp.set('community', next.community);
    if (next.page) sp.set('page', String(next.page));
    const onPlace = Boolean(params.province) && !changes.leavePlace;
    if (!onPlace && next.province && !changes.leavePlace) sp.set('province', next.province);
    const path = onPlace ? location.pathname : '/jobs';
    navigate(`${path}${sp.toString() ? `?${sp}` : ''}`);
    if (changes.page !== undefined) window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function submit(e) {
    e.preventDefault();
    // A typed location replaces the place page's location.
    update({ q: q.trim(), location: where.trim(), ...(where.trim() && params.province ? { leavePlace: true, province: '' } : {}) });
  }

  const r = state.result;
  const hasFilters = Boolean(f.q || f.location || f.category || f.community || f.province);
  const facets = (r && r.facets) || { province: {}, category: {}, community: {} };
  const topCities = (state.places || []).filter((p) => p.city).slice(0, 12);

  return (
    <JobsLayout>
      {/* Search band */}
      <section className="text-white" style={{ backgroundColor: INK }}>
        <div className="mx-auto max-w-6xl px-4 pb-10 pt-8 sm:px-6 sm:pt-12">
          {placeName && (
            <p className="mb-2 text-sm text-white/60">
              <Link to="/jobs" className="hover:text-white">
                Jobs
              </Link>
              {params.city && (
                <>
                  {' › '}
                  <Link to={placePath(f.province)} className="hover:text-white">
                    {provinceName(f.province)}
                  </Link>
                </>
              )}
            </p>
          )}
          <h1 className="font-[Poppins] text-3xl font-semibold tracking-tight sm:text-5xl">{heading}</h1>
          <p className="mt-2 text-white/70">Find your next job with employers across Canada.</p>
          <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-2 rounded-2xl bg-white p-2 shadow-xl transition focus-within:ring-4 focus-within:ring-white/25 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]" role="search">
            <label className="flex items-center gap-2 rounded-xl px-3 text-slate-900">
              <SearchIcon className="h-5 w-5 flex-shrink-0 text-slate-400" aria-hidden="true" />
              <span className="sr-only">Job title, keyword or company</span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                maxLength={100}
                placeholder="Job title, keyword or company"
                className="w-full border-0 bg-transparent py-3 text-base placeholder-slate-400 focus:outline-none focus:ring-0"
              />
            </label>
            <label className="flex items-center gap-2 rounded-xl px-3 text-slate-900 sm:border-l sm:border-slate-200">
              <LocationMarkerIcon className="h-5 w-5 flex-shrink-0 text-slate-400" aria-hidden="true" />
              <span className="sr-only">City or province</span>
              <input
                value={where}
                onChange={(e) => setWhere(e.target.value)}
                maxLength={100}
                placeholder={placeName || 'City or province'}
                className="w-full border-0 bg-transparent py-3 text-base placeholder-slate-400 focus:outline-none focus:ring-0"
              />
            </label>
            <button type="submit" className="cursor-pointer rounded-xl bg-primary px-6 py-3 font-semibold text-white hover:bg-red-800">
              Search
            </button>
          </form>
        </div>
      </section>

      <main className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[240px_minmax(0,1fr)]">
        {/* Filters */}
        <aside className="min-w-0 text-sm" aria-label="Filters">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            className="mb-2 inline-flex w-full cursor-pointer items-center justify-between rounded-xl border border-slate-300 bg-white px-4 py-2.5 font-semibold text-slate-800 lg:hidden"
          >
            Filters{[f.category, f.community, f.province].filter(Boolean).length ? ` (${[f.category, f.community, f.province].filter(Boolean).length})` : ''}
            <span className="text-slate-400">{showFilters ? 'Hide' : 'Show'}</span>
          </button>
          <div className={`space-y-6 ${showFilters ? 'block' : 'hidden'} lg:block`}>
          <div>
            <p className="mb-2 font-semibold text-slate-900">Job type</p>
            <div className="flex flex-wrap gap-2">
              {TYPES.map((t) => (
                <Chip key={t} active={f.category === t} onClick={() => update({ category: f.category === t ? '' : t })}>
                  {t}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 font-semibold text-slate-900">Province</p>
            {/* No counts, alphabetical (owner): the list shows where jobs are without ranking provinces. */}
            {!r && !state.error && <Spinner label="Loading provinces…" className="ml-2 h-5 w-5" />}
            <ul className="space-y-1">
              {Object.keys(facets.province)
                .sort((a, b) => provinceName(a).localeCompare(provinceName(b)))
                .map((code) => (
                  <li key={code}>
                    <Link
                      to={placePath(code)}
                      className={`block rounded-md px-2 py-1 hover:bg-white ${f.province === code ? 'bg-white font-semibold text-primary' : 'text-slate-700'}`}
                    >
                      {provinceName(code)}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
          <div>
            <p className="mb-2 font-semibold text-slate-900">Community</p>
            <div className="flex flex-wrap gap-2">
              {COMMUNITIES.map((c) => (
                <Chip key={c.slug} active={f.community === c.slug} onClick={() => update({ community: f.community === c.slug ? '' : c.slug })}>
                  {c.label}
                </Chip>
              ))}
            </div>
          </div>
          {hasFilters && (
            <Link to="/jobs" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
              <XIcon className="h-4 w-4" aria-hidden="true" /> Clear all
            </Link>
          )}
          </div>
        </aside>

        {/* Results */}
        <section className="min-w-0" aria-live="polite">
          {state.error && <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{state.error}</p>}
          {!r && !state.error && <LoadingBlock label="Finding jobs…" minHeight="min-h-[24rem]" />}
          {r && r.results.length === 0 && state.loading && <LoadingBlock label="Finding jobs…" minHeight="min-h-[24rem]" />}
          {r && r.results.length === 0 && !state.loading && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center">
              <p className="font-[Poppins] text-lg font-semibold">No jobs match your search</p>
              <p className="mt-1 text-sm text-slate-600">Try fewer words, another location, or clear the filters.</p>
              <Link to="/jobs" className="mt-4 inline-block rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
                See all jobs
              </Link>
            </div>
          )}
          {r && r.results.length > 0 && (
            <div className="relative">
              {state.loading && <LoadingOverlay label="Loading jobs…" />}
              <ul className="space-y-3">
                {r.results.map((job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </ul>
            </div>
          )}
          {r && r.results.some((c) => c.external) && (
            <p className="mt-4 flex items-start gap-2 text-xs text-slate-500">
              <ExternalLinkIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              Jobs marked “via … careers site” are posted on the employer’s own website. Selecting one opens it there, where you apply directly.
            </p>
          )}
          {r && r.pages > 1 && (
            <nav className="mt-6 flex items-center justify-between text-sm" aria-label="Pages">
              <button
                type="button"
                disabled={r.page === 0}
                onClick={() => { update({ page: r.page - 1 }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-slate-300 bg-white px-4 py-2 font-medium disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" /> Previous
              </button>
              <span className="text-slate-600">
                Page {r.page + 1} of {r.pages}
              </span>
              <button
                type="button"
                disabled={r.page + 1 >= r.pages}
                onClick={() => { update({ page: r.page + 1 }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-slate-300 bg-white px-4 py-2 font-medium disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </nav>
          )}

          {!hasFilters && topCities.length > 0 && (
            <div className="mt-10">
              <h2 className="font-[Poppins] text-lg font-semibold">Jobs by city</h2>
              <ul className="mt-3 flex flex-wrap gap-2">
                {topCities.map((p) => (
                  <li key={`${p.province}-${p.city}`}>
                    <Link to={placePath(p.province, p.city)} className="inline-block rounded-full border border-slate-300 bg-white px-3 py-1 text-sm text-slate-700 hover:border-slate-500">
                      {p.city}, {p.province}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="font-[Poppins] text-lg font-semibold">Hiring?</p>
              <p className="text-sm text-slate-600">Post once and reach job seekers on all five CanadaJobLeads community boards.</p>
            </div>
            <Link to="/" className="mt-3 inline-block rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-red-800 sm:mt-0">
              Post a job
            </Link>
          </div>
        </section>
      </main>
    </JobsLayout>
  );
}
